// Echtzeit: Notification vom Typ REMINDER (Socket-Ereignis NOTIFICATION_CREATE) → sofort Toast + Listen neu laden.
//
// Vertragslücke (docs/API.md 3.10/4.4): Welche Felder `NotificationDto.data` bei REMINDER enthält, ist nicht
// festgelegt. Annahme: `data.reminderId` (String) = ID der Erinnerung, optional `data.remindAt` (sonst Zeitpunkt aus dem Cache bzw. `createdAt`). Fehlt
// `reminderId`, dient die Notification-ID als Toast-ID; dann ist kein „Erledigt" möglich und ein zusätzlicher
// Toast durch den ReminderWatcher kann nicht ausgeschlossen werden.
import type { NotificationDto, ReminderDto } from '@lagerfeuer/shared';
import type { QueryClient } from '@tanstack/react-query';
import { messageLink } from '../api';
import { markReminderShown, REMINDERS_KEY, showReminderToast, wasReminderShown } from './notify';

export { REMINDERS_KEY, remindersListKey } from './notify';

function cachedReminder(queryClient: QueryClient, id: string): ReminderDto | undefined {
  for (const [, data] of queryClient.getQueriesData<ReminderDto[]>({ queryKey: REMINDERS_KEY })) {
    const hit = data?.find((r) => r.id === id);
    if (hit) return hit;
  }
  return undefined;
}

export function handleReminderNotification(queryClient: QueryClient, n: NotificationDto): void {
  if (n.type !== 'REMINDER') return;
  const reminderId = typeof n.data?.reminderId === 'string' ? n.data.reminderId : null;
  const id = reminderId ?? n.id;
  const cached = reminderId ? cachedReminder(queryClient, reminderId) : undefined;
  // Immer `id@remindAt` merken: ohne data.remindAt und Cache-Eintrag dient der Zeitpunkt der Notification.
  const remindAt = typeof n.data?.remindAt === 'string' ? n.data.remindAt : (cached?.remindAt ?? n.createdAt);
  if (!wasReminderShown(id, remindAt)) {
    markReminderShown(id, remindAt);
    showReminderToast(queryClient, {
      id,
      note: cached?.note ?? n.body ?? null,
      text: n.title,
      href: n.channelId ? messageLink(n.serverId, n.channelId, n.messageId) : undefined,
      canComplete: reminderId !== null,
    });
  }
  void queryClient.invalidateQueries({ queryKey: REMINDERS_KEY });
}
